/** @jsx jsx */
import React, { ComponentProps, useEffect, useState, useMemo } from "react";
import { css, jsx } from "@emotion/core";
import FlexBox from "components/atoms/FlexBox";
import FlexBoxItem from "components/atoms/FlexBoxItem";
import OrganizationFilteredTree from "components/organismos/organization/OrganizationFilteredTree/OrganizationFilteredTree";
import DatePickerForm from "components/molecules/DatePickerForm";
import FormLabel from "components/atoms/Form/FormLabel";
import RadioSelectForm from "components/molecules/RadioSelectForm";
import { RadioItem } from "components/molecules/RadioSelector";
import useSearchDate from "hooks/useSearchDate";
import moment from "moment";
import MultipleCheckboxForm, {
  CheckboxItem
} from "components/molecules/MultipleCheckboxForm";
import FormField from "components/atoms/Form/FormField";
import Select from "components/atoms/Select";
import VerticalLabelMutilSelectForm from "components/molecules/VerticalLabelMutilSelectForm";
import { useDebounce } from "components/organismos/SearchPanel/useDebounce";

// SearchFieldTypeを必要な3種類に限定
type SearchFieldType =
  | "date"
  | "radio"
  | "dateRange"
  | "select"
  | "organization"
  | "checkbox";

export type OrganizationFilteredTreeValue = {
  orgCode: string;
  orgName: string;
  categoryAry: Array<any> | undefined;
};

// 検索条件のモックデータの型定義
export interface SearchOptionItem {
  id: string;
  value: string;
  label: string;
}

export type FromToDateValue = {
  from: Date | string | null;
  to: Date | string | null;
};

// 各タイプの additionalProps の型
interface DatePickerProps {
  dateFormat?: string;
  isFullWidth?: boolean;
  showMonthYearPicker?: boolean;
}

interface RadioSelectProps {
  items: RadioItem[];
}

interface CheckboxProps {
  items: CheckboxItem[];
}

export interface OrganizationFilteredTreeProps {
  functionType: number;
  targetdayForm?: Date;
  targetdayTo?: Date;
  orgLabel?: string;
  initOrgValue?: string | string[];
  addAllItem?: boolean;
  isShowSituation?: boolean;
}

// `additionalProps` を条件付きで適用する型
type AdditionalProps<T extends SearchFieldType> = T extends "date"
  ? DatePickerProps
  : T extends "fromToDate"
  ? DatePickerProps
  : T extends "radio"
  ? RadioSelectProps
  : T extends "checkbox"
  ? CheckboxProps
  : T extends "select"
  ? ComponentProps<typeof Select>
  : T extends "organization"
  ? OrganizationFilteredTreeProps
  : never;

export type SearchField<T> = {
  key: keyof T;
  label: string;
  type: SearchFieldType;
  initialValue: T[keyof T] | FromToDateValue | string; // 文字列も受け取れるように追加
  row: number;
  additionalProps?: AdditionalProps<SearchFieldType>;
};

type SearchPanelProps<T> = {
  searchFields: SearchField<T>[];
  onSearchChange: (params: Partial<T>) => void;
};

// 型ガード関数
function isFromToDateValue(value: any): value is FromToDateValue {
  return (
    value &&
    typeof value === "object" &&
    "from" in value &&
    "to" in value &&
    (value.from === null ||
      value.from instanceof Date ||
      typeof value.from === "string") &&
    (value.to === null ||
      value.to instanceof Date ||
      typeof value.to === "string")
  );
}

function isOrganizationFilteredTreeValue(
  value: any
): value is OrganizationFilteredTreeValue {
  // 空のオブジェクトの場合はデフォルト値を設定
  if (value && typeof value === "object" && Object.keys(value).length === 0) {
    return true;
  }

  return (
    value &&
    typeof value === "object" &&
    "orgCode" in value &&
    "orgName" in value &&
    "categoryAry" in value &&
    typeof value.orgCode === "string" &&
    typeof value.orgName === "string" &&
    (value.categoryAry === undefined ||
      value.categoryAry === null ||
      value.categoryAry instanceof Array)
  );
}

// 安全に日付文字列を評価する関数
export const evaluateDateString = (dateStr: string): Date => {
  if (dateStr === "new Date()") {
    return new Date();
  }

  // 他の日付形式のパターンも処理
  // 例: "new Date('2023-12-01')", "new Date(2023, 11, 1)" などの形式
  const singleQuoteMatch = dateStr.match(/^new Date\(['"](.+?)['"]\)$/);
  if (singleQuoteMatch) {
    const dateParam = singleQuoteMatch[1];
    const date = new Date(dateParam);
    return isNaN(date.getTime()) ? new Date() : date;
  }

  // 数値パラメータの場合（年、月、日など）
  const numericMatch = dateStr.match(/^new Date\((.+?)\)$/);
  if (numericMatch) {
    const params = numericMatch[1].split(",").map(p => p.trim());
    if (params.length >= 3) {
      const year = parseInt(params[0], 10);
      const month = parseInt(params[1], 10); // JavaScriptの月は0ベース
      const day = parseInt(params[2], 10);

      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        const date = new Date(year, month, day);
        return isNaN(date.getTime()) ? new Date() : date;
      }
    }
  }

  const date = new Date(dateStr);

  return isNaN(date.getTime()) ? new Date() : date;
};

const SearchPanel = <T,>({
  searchFields,
  onSearchChange
}: SearchPanelProps<T>) => {
  const d = new Date();
  d.setDate(d.getDate() - 1);

  const searchDate = useSearchDate(moment(d.toString()).format("YYYY-MM-DD"));

  const [targetDayFrom, setTargetDayFrom] = useState(
    searchDate.fromDate ? new Date(searchDate.fromDate) : undefined
  );
  const [targetDayTo, setTargetDayTo] = useState(
    searchDate.toDate ? new Date(searchDate.toDate) : undefined
  );

  useEffect(() => {
    if (searchDate.fromDate && searchDate.toDate) {
      setTargetDayFrom(new Date(searchDate.fromDate));
      setTargetDayTo(new Date(searchDate.toDate));
    }
  }, [searchDate.fromDate, searchDate.toDate]);

  const [searchParams, setSearchParams] = useState<Partial<T>>({});
  const [fieldsByRow, setFieldsByRow] = useState<any>({});
  const [orgs, setOrgs] = useState<any>([]);
  const staffCode = sessionStorage.getItem("loginUser.staffCode") || "";
  const debounce = useDebounce(2000);

  // 検索条件が変更されたときにレンダリングを強制的にリセットするためのメモ化
  const memoizedFieldsByRow = useMemo(() => {
    return fieldsByRow;
  }, [fieldsByRow, searchFields]);

  const searchPanelContainer = css({
    position: "relative",
    paddingTop: "10px"
  });

  const rowMargin = css({
    marginBottom: "10px", // 異なるrow間の余白
    gap: "24px"
  });

  useEffect(() => {
    // 検索パラメータの初期化
    const searchParams = searchFields.reduce((acc: any, field) => {
      if (field.type === "checkbox") {
        const props = field.additionalProps as CheckboxProps;

        // チェックボックスの場合は、各項目のIDごとに値を設定
        props.items.forEach((item: any) => {
          acc[item.id] = item.value;
        });
      } else if ("initialValue" in field) {
        // その他の通常フィールドの場合は、初期値を設定
        let processedValue: any = field.initialValue;

        // date や dateRange の場合、文字列の "new Date()" を実際のDateオブジェクトに変換
        if (field.type === "date" && typeof field.initialValue === "string") {
          const dateValue = evaluateDateString(field.initialValue);
          if (dateValue) {
            processedValue = dateValue;
          } else {
            console.warn(`Invalid date expression: ${field.initialValue}`);
            processedValue = null;
          }
        } else if (field.type === "dateRange") {
          const dateValue: any = field.initialValue;
          if (dateValue) {
            processedValue = {
              from: evaluateDateString(dateValue.from),
              to: evaluateDateString(dateValue.to)
            };
          } else {
            console.warn(`Invalid date expression: ${field.initialValue}`);
            processedValue = {
              from: null,
              to: null
            };
          }
        }

        if (field.type === "organization") {
          processedValue = {
            orgCode: (field.additionalProps as OrganizationFilteredTreeProps)
              .addAllItem
              ? ""
              : sessionStorage.getItem("loginUser.orgCode") || "",
            orgName: "",
            categoryAry:
              JSON.parse(
                String(
                  sessionStorage.getItem(
                    `sessionOrganizationFileterTree${window.location.pathname}.Selected`
                  )
                )
              ) || undefined
          };
        }

        acc[field.key] = processedValue as T[keyof T];
      }
      return acc;
    }, {} as Partial<T>);

    // row値でフィールドをグループ化する
    const fieldsByRow = searchFields.reduce((acc, field) => {
      if (!acc[field.row]) {
        acc[field.row] = [];
      }
      acc[field.row].push(field);
      return acc;
    }, {} as Record<number, SearchField<T>[]>);

    setFieldsByRow(fieldsByRow);
    setSearchParams(searchParams);

    debounce(() => {
      onSearchChange(searchParams);
    });
  }, [searchFields]);

  useEffect(() => {
    // レンダリングを引き起こさずに組織名を更新
    // @ts-ignore
    if (searchParams["organization"] && orgs.length > 0) {
      // @ts-ignore
      const currentOrg = searchParams["organization"];
      const foundOrg = orgs.find(
        // @ts-ignore
        (item: any) => item.value === currentOrg?.orgCode
      );

      if (foundOrg && foundOrg.name !== currentOrg.orgName) {
        // stateを変更せずに直接オブジェクトを更新（レンダリングは発生しない）
        // @ts-ignore
        currentOrg.orgName = foundOrg.name;
      }
    }
  }, [orgs, searchParams]);

  const handleChange = (key: any, value: any) => {
    setSearchParams(prev => {
      const updatedParams = { ...prev, [key]: value };

      debounce(() => {
        onSearchChange(updatedParams);
      });

      return updatedParams;
    });
  };

  const renderField = (field: SearchField<T>) => {
    const value = searchParams[field.key];

    switch (field.type) {
      case "organization": {
        if (!isOrganizationFilteredTreeValue(value)) {
          return null;
        }
        const organizationFilteredTreeValue = value;

        return (field.additionalProps as OrganizationFilteredTreeProps)
          .addAllItem !== null &&
          targetDayFrom &&
          targetDayTo ? (
          <div
            css={css({
              width: "100%"
            })}
          >
            <OrganizationFilteredTree
              key={String(field.key)}
              staffCode={staffCode}
              targetdayForm={targetDayFrom}
              targetdayTo={targetDayTo}
              orgLabel={field.label}
              orgCallback={(org: any) => {
                handleChange(field.key, {
                  ...organizationFilteredTreeValue,
                  orgCode: Array.isArray(org) ? org[0] : org,
                  orgName: "",
                  categoryAry: organizationFilteredTreeValue.categoryAry || []
                } as T[keyof T]);
              }}
              orgCategoryCallback={(args: Array<string>) => {
                // @ts-ignore
                if (args !== searchParams[field.key].categoryArr) {
                  handleChange(field.key, {
                    ...organizationFilteredTreeValue,
                    categoryAry: args
                  } as T[keyof T]);
                }
              }}
              {...(field.additionalProps as OrganizationFilteredTreeProps)}
              initOrgValue={
                (field.additionalProps as OrganizationFilteredTreeProps)
                  .addAllItem
                  ? ""
                  : sessionStorage.getItem("loginUser.orgCode") || ""
              }
              orgOrgOptionListCallback={(orgOptionList: any) => {
                setOrgs(orgOptionList);
              }}
            />
          </div>
        ) : null;
      }

      case "radio":
        return (
          <FlexBox key={String(field.key)}>
            {field.label && (
              <FlexBoxItem
                minWidth="100px"
                customStyle={css({ marginRight: "16px" })}
              >
                <FormLabel label={field.label} />
              </FlexBoxItem>
            )}
            <FlexBoxItem>
              <FormField>
                <RadioSelectForm
                  label=""
                  {...(field.additionalProps as RadioSelectProps)}
                  value={String(value)}
                  setValue={e => {
                    const value = e.target.value as unknown;

                    if (value) {
                      handleChange(field.key, value as T[keyof T]);
                    }
                  }}
                  name={field.key as string}
                />
              </FormField>
            </FlexBoxItem>
          </FlexBox>
        );

      case "date":
        return (
          <FlexBox key={String(field.key)}>
            {field.label && (
              <FlexBoxItem width="100px">
                <FormLabel label={field.label} />
              </FlexBoxItem>
            )}
            <FlexBoxItem>
              <DatePickerForm
                label=""
                isFullWidth={true}
                dateFormat="yyyy年MM月dd日"
                date={value instanceof Date ? value : null}
                changeDate={(date: Date) => {
                  if (date instanceof Date && date !== null) {
                    const additionalProps = field.additionalProps as DatePickerProps;
                    let processedDate = date;

                    // showMonthYearPickerがtrueの場合、月初の値をセット
                    if (additionalProps?.showMonthYearPicker) {
                      processedDate = new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        1
                      );
                    }

                    handleChange(
                      field.key,
                      (processedDate as unknown) as T[keyof T]
                    );
                  }
                }}
                {...(field.additionalProps as DatePickerProps)}
              />
            </FlexBoxItem>
          </FlexBox>
        );

      case "dateRange":
        if (!isFromToDateValue(value)) {
          return null;
        }
        const fromToValue = value;
        return (
          <FlexBox key={String(field.key)}>
            {field.label && (
              <FlexBoxItem width="100px">
                <FormLabel label={field.label} />
              </FlexBoxItem>
            )}

            <FlexBoxItem>
              <DatePickerForm
                label=""
                date={fromToValue.from ? new Date(fromToValue.from) : null}
                isFullWidth={true}
                changeDate={(fromDate: Date) => {
                  if (fromDate !== null) {
                    const additionalProps = field.additionalProps as DatePickerProps;
                    let processedFromDate = fromDate;

                    // showMonthYearPickerがtrueの場合、月初の値をセット
                    if (additionalProps?.showMonthYearPicker) {
                      processedFromDate = new Date(
                        fromDate.getFullYear(),
                        fromDate.getMonth(),
                        1
                      );
                    }

                    handleChange(field.key, ({
                      ...fromToValue,
                      from: processedFromDate
                    } as unknown) as T[keyof T]);
                  }
                }}
                {...(field.additionalProps as DatePickerProps)}
              />
            </FlexBoxItem>
            <FlexBoxItem>
              <span style={{ margin: "0 30px" }}>～</span>
            </FlexBoxItem>
            <FlexBoxItem>
              <DatePickerForm
                label=""
                date={fromToValue.to ? new Date(fromToValue.to) : null}
                isFullWidth={true}
                changeDate={(toDate: Date) => {
                  if (toDate !== null) {
                    const additionalProps = field.additionalProps as DatePickerProps;
                    let processedToDate = toDate;

                    // showMonthYearPickerがtrueの場合、月初の値をセット
                    if (additionalProps?.showMonthYearPicker) {
                      processedToDate = new Date(
                        toDate.getFullYear(),
                        toDate.getMonth(),
                        1
                      );
                    }

                    handleChange(field.key, ({
                      ...fromToValue,
                      to: processedToDate
                    } as unknown) as T[keyof T]);
                  }
                }}
                {...(field.additionalProps as DatePickerProps)}
              />
            </FlexBoxItem>
          </FlexBox>
        );

      case "checkbox":
        return (
          <FlexBox key={String(field.key)}>
            {field.label && (
              <FlexBoxItem
                minWidth="100px"
                customStyle={css({ marginRight: "16px" })}
              >
                <FormLabel label={field.label} />
              </FlexBoxItem>
            )}
            <FlexBoxItem>
              <MultipleCheckboxForm
                name={String(field.key)}
                label={""}
                setValue={e =>
                  handleChange(e.target.id, !(e.target.value === "true"))
                }
                {...(field.additionalProps as CheckboxProps)}
                items={(field.additionalProps as CheckboxProps).items.map(
                  item => ({
                    ...item,
                    // @ts-ignore
                    value: searchParams[item.id] || false
                  })
                )}
              />
            </FlexBoxItem>
          </FlexBox>
        );

      case "select":
        const props = field.additionalProps as ComponentProps<typeof Select>;
        return (
          <FlexBox key={String(field.key)}>
            {field.label && (
              <FlexBoxItem
                minWidth="100px"
                customStyle={css({ marginRight: "16px" })}
              >
                <FormLabel label={field.label} />
              </FlexBoxItem>
            )}
            <FlexBoxItem
              customStyle={css({
                minWidth: "20rem"
              })}
            >
              {props.isMulti ? (
                <VerticalLabelMutilSelectForm
                  label=""
                  values={props.options.filter(option => {
                    const values = searchParams[field.key] as any;
                    if (!values) return false;
                    if (values.includes(option.value)) {
                      return true;
                    }
                    return false;
                  })}
                  setValue={(val: string) => {
                    const values = searchParams[field.key] as any;
                    const removeItems: Array<string> = [];
                    props.options.forEach(option => {
                      if (values.includes(option.value)) {
                        removeItems.push(option.value);
                      }
                    });
                    const tempList = values.filter((item: string) => {
                      if (removeItems.includes(item) || item === "") {
                        return false;
                      }
                      return true;
                    });
                    const valList = val.split(",");
                    if (
                      valList.length > 0 &&
                      valList[valList.length - 1] === ""
                    ) {
                      valList.splice(valList.length - 1, 1);
                    }
                    tempList.push.apply(tempList, valList);

                    handleChange(field.key, tempList);
                  }}
                  options={props.options}
                  isMulti={props.isMulti}
                  name={String(field.key)}
                />
              ) : (
                <Select
                  value={String(value)}
                  setValue={(val: any) => {
                    handleChange(field.key, val);
                  }}
                  {...(field.additionalProps as ComponentProps<typeof Select>)}
                  name={String(field.key)}
                />
              )}
            </FlexBoxItem>
          </FlexBox>
        );
      default:
        return null;
    }
  };

  return (
    <div css={searchPanelContainer}>
      {/* 検索条件フィールドの表示 */}
      <div key={JSON.stringify(searchFields)}>
        {Object.keys(memoizedFieldsByRow)
          .map(Number)
          .sort((a, b) => a - b)
          .map(rowNumber => (
            <FlexBox
              key={`row-${rowNumber}-${JSON.stringify(searchFields)}`}
              customStyle={rowMargin}
            >
              {memoizedFieldsByRow[rowNumber].map((field: any) =>
                renderField(field)
              )}
            </FlexBox>
          ))}
      </div>
    </div>
  );
};

export default SearchPanel;
